import React, { useEffect, useState } from "react";
import axios from "axios";
import { apiBaseUrl, authHeaders } from "./shared";

// Trailers are real inventory now (synced from Motive's /v1/assets), but
// deliberately NOT linked to a specific truck -- there's no reliable way to
// know current pairing (see the redesign plan), so this is two separate
// real lists, not a combined "truck+trailer" row implying a fixed pairing.
// Same fallback rule backend/driver_lookup.py's resolve_driver_for_truck
// uses for load-matching (current_driver_id is Motive's live snapshot --
// authoritative when present, but goes null whenever a driver logs off or
// goes off_duty/sleeper even though the assignment is still real; drivers.
// last_truck_id is the sticky, driver-owned mirror that survives a rest
// period) -- re-implemented client-side here since this is a bulk list
// covering every truck at once, not a single-truck negotiation call.
function resolveDriverForTruck(truck, driversByLastTruckId) {
  if (truck.current_driver_id) {
    return { name: truck.current_driver_id.display_value, isReturning: false };
  }
  const candidates = driversByLastTruckId[truck.id] || [];
  if (candidates.length === 0) return { name: null, isReturning: false };
  // Most recently updated wins if more than one driver ever had this truck
  // as their last known one (reassignments over time).
  const driver = [...candidates].sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))[0];
  return { name: driver.driver_name, isReturning: true };
}

export default function TrucksTrailersPage() {
  const [trucks, setTrucks] = useState([]);
  const [trailers, setTrailers] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${apiBaseUrl}/trucks/?limit=1000`, authHeaders()),
      axios.get(`${apiBaseUrl}/trailers/?limit=200`, authHeaders()),
      axios.get(`${apiBaseUrl}/drivers/?limit=1000`, authHeaders()),
    ])
      .then(([tRes, trRes, dRes]) => {
        setTrucks(tRes.data.records || tRes.data || []);
        setTrailers(trRes.data.records || trRes.data || []);
        setDrivers(dRes.data.records || dRes.data || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const driversByLastTruckId = {};
  for (const d of drivers) {
    const lastTruckId = d.last_truck_id?.id || d.last_truck_id;
    if (!lastTruckId) continue;
    (driversByLastTruckId[lastTruckId] ||= []).push(d);
  }

  return (
    <div>
      <div style={styles.header}>
        <h1 style={styles.title}>Trucks & Trailers</h1>
        <div style={styles.subtitle}>Trucks and trailers are separate fleet inventory -- see the load-history note on equipment type.</div>
      </div>

      <div style={styles.card}>
        <div style={styles.cardTitle}>Trucks ({loading ? "…" : trucks.length})</div>
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Unit</th>
                <th style={styles.th}>Make / Model</th>
                <th style={styles.th}>Driver</th>
                <th style={styles.th}>Equipment type</th>
              </tr>
            </thead>
            <tbody>
              {trucks.map((t) => {
                const { name, isReturning } = resolveDriverForTruck(t, driversByLastTruckId);
                return (
                  <tr key={t.id}>
                    <td style={styles.td}>{t.unit_number}</td>
                    <td style={styles.td}>{t.make} {t.model}</td>
                    <td style={styles.td}>
                      {name || "Unassigned"}
                      {isReturning && (
                        <span style={styles.motiveUnassignedBadge} title="Motive's live snapshot shows no driver currently in this truck -- this is the last driver known to have it, not confirmed live">
                          Motive: unassigned
                        </span>
                      )}
                    </td>
                    <td style={styles.td}>{t.equipment_type_id?.display_value || "—"}</td>
                  </tr>
                );
              })}
              {!loading && trucks.length === 0 && <tr><td style={styles.td} colSpan={4}>No trucks yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ ...styles.card, marginTop: 16 }}>
        <div style={styles.cardTitle}>Trailers ({loading ? "…" : trailers.length})</div>
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Unit</th>
                <th style={styles.th}>Type</th>
                <th style={styles.th}>Axles</th>
                <th style={styles.th}>Length (ft)</th>
                <th style={styles.th}>VIN</th>
              </tr>
            </thead>
            <tbody>
              {trailers.map((t) => (
                <tr key={t.id}>
                  <td style={styles.td}>{t.unit_number}</td>
                  <td style={styles.td}>{t.equipment_type_id?.display_value || "—"}</td>
                  <td style={styles.td}>{t.axle_count ?? "—"}</td>
                  <td style={styles.td}>{t.length_ft ?? "—"}</td>
                  <td style={styles.td}>{t.vin || "—"}</td>
                </tr>
              ))}
              {!loading && trailers.length === 0 && <tr><td style={styles.td} colSpan={5}>No trailers yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const styles = {
  header: { marginBottom: 20 },
  title: { fontSize: 22, fontWeight: 800, color: "var(--ink)", margin: 0 },
  subtitle: { fontSize: 13, color: "var(--muted)", marginTop: 4 },
  card: {
    background: "var(--card)", border: "1px solid var(--line)", borderRadius: "var(--radius-lg)",
    padding: 18, boxShadow: "var(--shadow-xs)",
  },
  cardTitle: { fontSize: 14, fontWeight: 700, color: "var(--ink)", marginBottom: 12 },
  motiveUnassignedBadge: {
    marginLeft: 8, padding: "1px 7px", borderRadius: 999, fontSize: 10, fontWeight: 700,
    background: "rgba(242,163,61,0.15)", color: "#c07f1f",
  },
  tableWrap: { overflowX: "auto", maxHeight: 320, overflowY: "auto" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 12.5 },
  th: { textAlign: "left", padding: "8px 10px", color: "var(--muted)", fontWeight: 600, borderBottom: "1px solid var(--line)", position: "sticky", top: 0, background: "var(--card)" },
  td: { padding: "8px 10px", color: "var(--ink-2)", borderBottom: "1px solid var(--line)" },
};
